import { ReactNode, SyntheticEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Flex, Tooltip } from 'antd';
import { Button, Form, FormItem, InputSearch, Text } from '../../Ant';
import { Close, Search } from '../../icons';
import FavouriteCta from '../../favouriteCta/favouriteCta';
import { MailMetadata } from '../../../interface/mailMetadata';
import { IMatter } from '../../../interface/matter';
import { useFetchMatters } from '../../../hooks/useFetchMatters';
import { useDebouncedSearch } from '../../../hooks/useDebouncedSearch';
import { useFeatureContext } from '../../../context/FeatureContext';
import ShowMetadataDetails from './showMetadataDetails';
import MailMatterCard from './mailMatterCard';
import SuggestedMatters from './suggestedMatters';
import MailMatterList from './mailMatterList';
import TransactionActionPanel from './transactionActionPanel';
import LinkedItemNotFoundModal from './linkedItemNotFoundModal';
import { MatterStatus } from '../../../constants/matter';
import SkeletonCardList from '../../common/skeleton/skeletonCardList';
import { useListViewPreference } from '../../../context/ListViewPreferenceContext';
import { isKeyboardActivation } from '../../common/activationProps';

interface EntriesTabMailProps {
  mailSubject: string;
  mailMetadata: MailMetadata | null;
  emailRecipients: string[];
  selectedMatter: IMatter | undefined;
  setSelectedMatter: React.Dispatch<React.SetStateAction<IMatter | undefined>>;
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  matterDetails: IMatter | undefined;
  isMatterDetailsLoading: boolean;
  matterDetailsError: Error | null;
  handleAddTransaction: () => void;
  handleAddTask: (type: string) => void;
  handleLinkToMatter: () => void;
  handleUnlinkToMatter: () => void;
  onItemUnlinked?: () => void;
  disableMatterLinking?: boolean;
}

const EntriesTabMail = ({
  mailSubject,
  mailMetadata,
  emailRecipients,
  selectedMatter,
  setSelectedMatter,
  expanded,
  setExpanded,
  matterDetails,
  isMatterDetailsLoading,
  matterDetailsError,
  handleAddTransaction,
  handleAddTask,
  handleLinkToMatter,
  handleUnlinkToMatter,
  onItemUnlinked,
  disableMatterLinking,
}: EntriesTabMailProps) => {
  const { t } = useTranslation();
  const { features } = useFeatureContext();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState<string>('');
  const { getActiveFavorite } = useListViewPreference();
  const [isFavourite, setIsFavourite] = useState<boolean>(getActiveFavorite('matters'));
  const tabRef = useRef<HTMLDivElement>(null);

  const {
    data: matterList,
    isLoading: isMatterLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchMatters({
    sortBy: ['createdOn:DESC'],
    isFavorite: isFavourite,
    withFavoriteInformation: true,
    pageSize: 10,
    extraFilters: {
      status: MatterStatus.ACTIVE,
    },
    search: searchValue,
    withDefaultTransactionType: true,
    withHasLinkedTransactionTypes: true,
    relations: ['principalCompany:name', 'principalPrivatePerson:fullName'],
    fields: [
      'subject',
      'matterNumber',
      'status',
      'invoiceableType',
      'openingDate',
      'hasImportantNotes',
      'currency',
      'language',
      'createdOn',
    ],
  });

  const { search } = useDebouncedSearch(
    (query: string, abortSignal: AbortSignal) => {
      if (abortSignal.aborted) return;
      setSearchValue(query);
    },
    { allowEmptySearch: true },
  );

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value ?? '';
    search(value);
  };
  const selectMatter = (matter: IMatter, event: SyntheticEvent) => {
    setSelectedMatter(matter);
    if (!isKeyboardActivation(event)) return;
    tabRef.current?.querySelector<HTMLElement>('.action-btn')?.focus();
  };

  const handleRefreshMatters = () => {
    queryClient.removeQueries({ queryKey: ['matters-list'] });
    return refetch();
  };

  const matterSearchHeader = (refreshButton?: ReactNode) => (
    <FormItem label={t('matter:selectMatter')} required={true} className="entries-tab-label">
      <div className="cta-wrapper d-flex items-center justify-between gap-5 w-full">
        <InputSearch
          placeholder={t('matter:search')}
          prefix={<Search />}
          onChange={(e) => onSearch(e)}
          className="w-full"
          data-testid="search-input"
          data-list-search
          data-tab-to-results
          size="small"
          allowClear={{
            clearIcon: <Close className="custom-clear-icon" />,
          }}
        />
        {refreshButton}
        <FavouriteCta isFavourite={isFavourite} onToggle={setIsFavourite} />
      </div>
    </FormItem>
  );

  return (
    /* the tab pane above lays in the top gutter, so the tab only owns the one under its content */
    <Flex ref={tabRef} vertical className="h-full pb-gutter">
      <div className="w-full mb-gutter">
        <Form layout="vertical" className="w-full">
          <FormItem
            label={t('common:subject')}
            className="disabled mail-action-subject entries-tab-label"
          >
            <div className="relative">
              <Tooltip title={mailSubject} placement="bottom" arrow={false}>
                <Button
                  type="default"
                  data-testid="selected-mail-subject"
                  block
                  className="add-transaction-btn white-space-wrap"
                >
                  <div className="d-flex flex-col items-start selected-values">
                    <Text
                      $fontsize="13px"
                      $color="title"
                      $weight={400}
                      $lineheight="20px"
                      className="ta-left lineclamp-2"
                    >
                      {mailSubject}
                    </Text>
                  </div>
                </Button>
              </Tooltip>
            </div>
          </FormItem>
        </Form>
      </div>
      {!mailMetadata?.transactionId && !mailMetadata?.taskId && !mailMetadata?.criticalTaskId && (
        <div className="mail-list-container">
          {!mailMetadata?.matterId && emailRecipients?.length > 0 && (
            <SuggestedMatters
              handleSelectMatter={selectMatter}
              selectedMatterId={selectedMatter?.id}
              emailRecipients={emailRecipients}
              expanded={expanded}
              setExpanded={setExpanded}
            />
          )}
          <Form
            className="d-flex flex-col gap-10 flex-grow min-h-0 transaction-mail-form"
            layout="vertical"
          >
            {!mailMetadata?.matterId && (
              <MailMatterList
                header={matterSearchHeader}
                matters={matterList || []}
                isLoading={isMatterLoading}
                isFetching={isFetching}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                onSelectMatter={selectMatter}
                selectedMatterId={selectedMatter?.id}
                onRefresh={handleRefreshMatters}
                data-testid="matters-list"
              />
            )}
            {mailMetadata?.matterId && (
              <>
                {matterSearchHeader()}
                <FormItem className="mail-form-item">
                  <Flex gap={10} vertical className="h-full pb-gutter overflow-y-auto">
                    {matterDetails && (
                      <MailMatterCard
                        matter={matterDetails}
                        onClick={selectMatter}
                        isSelected={selectedMatter?.id === matterDetails?.id}
                      />
                    )}
                    {isMatterDetailsLoading && (
                      <SkeletonCardList rows={[1]} withSubcontentOnFirstRow withHeader={false} />
                    )}
                  </Flex>
                  {matterDetailsError && (
                    <LinkedItemNotFoundModal
                      title={t('matter:doesNotExist')}
                      onConfirm={handleUnlinkToMatter}
                    />
                  )}
                </FormItem>
              </>
            )}
          </Form>
        </div>
      )}
      {(mailMetadata?.transactionId || mailMetadata?.taskId || mailMetadata?.criticalTaskId) && (
        <ShowMetadataDetails
          metadata={mailMetadata}
          updateSelectedMatter={setSelectedMatter}
          onItemUnlinked={onItemUnlinked}
        />
      )}
      <TransactionActionPanel
        mailMetadata={mailMetadata}
        selectedMatter={selectedMatter}
        features={features}
        onAddTransaction={handleAddTransaction}
        onAddTask={handleAddTask}
        onLinkToMatter={handleLinkToMatter}
        onUnlinkToMatter={handleUnlinkToMatter}
        disableMatterLinking={disableMatterLinking}
      />
    </Flex>
  );
};

export default EntriesTabMail;
